import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { ChatWindow, toast } from '@utilities';
import { createChatMessage, createChatSession, readChatSession } from '@api/chat';

import { DEFAULT_ASSISTANT_MESSAGE } from '../../utilities/constants';

const NarrativesChatWindow = ({ initialInput = '', onFlowSelected, onFlowStateChange }) => {
  const [chatSessionId, setChatSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isInitializing, setIsInitializing] = useState(true);
  const [isAwaitingAssistant, setIsAwaitingAssistant] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const isMountedRef = useRef(true);
  const fallbackIdCounterRef = useRef(0);
  const emittedFlowIdRef = useRef(null);
  const hasSubmittedInitialInputRef = useRef(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const createFallbackMessageId = useCallback((role) => {
    fallbackIdCounterRef.current += 1;
    return `${role}-message-${fallbackIdCounterRef.current}-${Date.now()}`;
  }, []);

  const hydrateMessages = useCallback(
    async (sessionId) => {
      if (!sessionId) return;

      try {
        const {
          flowId: nextFlowId,
          flowState: nextFlowState,
          messages: nextMessages,
        } = await readChatSession({
          chatSessionId: sessionId,
          createFallbackId: createFallbackMessageId,
        });

        if (isMountedRef.current) {
          const hydrated =
            initialInput && nextMessages.length === 0
              ? [
                  ...nextMessages,
                  {
                    content: initialInput,
                    id: createFallbackMessageId('user'),
                    role: 'user',
                  },
                ]
              : nextMessages;

          setMessages(hydrated);
          setErrorMessage('');

          if (onFlowStateChange) {
            onFlowStateChange(nextFlowState || null);
          }

          if (onFlowSelected && nextFlowId && nextFlowId !== emittedFlowIdRef.current) {
            emittedFlowIdRef.current = nextFlowId;
            onFlowSelected(nextFlowId);
          }
        }
      } catch (error) {
        console.error('Failed to read chat session', error);
        if (isMountedRef.current) {
          setErrorMessage('Unable to load your chat history. Please try again.');
        }
      }
    },
    [createFallbackMessageId, initialInput, onFlowSelected, onFlowStateChange]
  );

  const initializeSession = useCallback(async () => {
    setIsInitializing(true);
    setErrorMessage('');

    try {
      const sessionId = await createChatSession();
      if (!sessionId) throw new Error('Chat service did not return a session identifier.');

      if (!isMountedRef.current) return;

      setChatSessionId(sessionId);
      await hydrateMessages(sessionId);
    } catch (error) {
      console.error('Failed to initialize chat session', error);
      if (isMountedRef.current) {
        setErrorMessage('We could not start a chat session. Please refresh and try again.');
      }
    } finally {
      if (isMountedRef.current) setIsInitializing(false);
    }
  }, [hydrateMessages]);

  useEffect(() => {
    initializeSession();
  }, [initializeSession]);

  const handleSubmit = useCallback(
    async (userInput) => {
      if (!chatSessionId) return;

      const trimmedInput = userInput?.trim();
      if (!trimmedInput) return;

      setIsAwaitingAssistant(true);
      setErrorMessage('');
      setInputValue('');

      try {
        await createChatMessage({ chatSessionId, userInput: trimmedInput });
        await hydrateMessages(chatSessionId);
      } catch (error) {
        console.error('Failed to create chat message', error);
        if (isMountedRef.current) {
          toast('Unable to send your question. Please try again.', { status: 'error' });
          setInputValue(trimmedInput);
        }
      } finally {
        if (isMountedRef.current) setIsAwaitingAssistant(false);
      }
    },
    [chatSessionId, hydrateMessages]
  );

  const displayedMessages = useMemo(() => {
    if (messages.length) return messages;

    if (errorMessage) {
      return [{ content: errorMessage, id: 'assistant-error', role: 'assistant' }];
    }

    if (isInitializing) {
      return [
        {
          content: 'Setting up your Narratives chat...',
          id: 'assistant-initializing',
          role: 'assistant',
        },
      ];
    }

    return [DEFAULT_ASSISTANT_MESSAGE];
  }, [messages, errorMessage, isInitializing]);

  const isInputDisabled = isInitializing || !chatSessionId || Boolean(errorMessage);

  useEffect(() => {
    if (!initialInput || !chatSessionId || messages.length > 0) return;
    if (hasSubmittedInitialInputRef.current) return;

    hasSubmittedInitialInputRef.current = true;

    handleSubmit(initialInput);
  }, [initialInput, chatSessionId, handleSubmit, messages.length]);

  return (
    <ChatWindow
      inputDisabled={isInputDisabled}
      inputValue={inputValue}
      isAgentStreaming={isAwaitingAssistant}
      messages={displayedMessages}
      onChangeInput={setInputValue}
      onSubmit={handleSubmit}
      renderMarkdown="assistant"
      submitDisabled={isInputDisabled}
    />
  );
};

NarrativesChatWindow.propTypes = {
  initialInput: PropTypes.string,
  onFlowSelected: PropTypes.func,
  onFlowStateChange: PropTypes.func,
};

export default NarrativesChatWindow;
